/**
 * ImageSizeComparison Component
 *
 * Displays image size comparison (original vs converted).
 * Shows dimensions, file size, and compression rate.
 */

import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/v2/ui';
import { formatFileSize } from '@/lib/api/publicConverterApi';
import { cn } from '@/lib/utils';
import { ArrowRight } from 'lucide-react';

export interface ImageSizeInfo {
  width: number;
  height: number;
  fileSize: number; // bytes
}

export interface ImageSizeComparisonProps {
  original: ImageSizeInfo | null;
  converted?: ImageSizeInfo | null;
  className?: string;
}

export function ImageSizeComparison({
  original,
  converted,
  className
}: ImageSizeComparisonProps) {
  const { t } = useTranslation();
  if (!original) return null;

  // Calculate compression rate
  const compressionRate = converted
    ? ((1 - converted.fileSize / original.fileSize) * 100).toFixed(1)
    : null;

  const isSmaller = converted && converted.fileSize < original.fileSize;

  return (
    <div className={cn(
      'p-6 bg-neutral-50 dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800',
      className
    )}>
      {/* Grid: Desktop side-by-side, Mobile stacked */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 md:gap-6 items-center">
        {/* Original Box */}
        <div className="bg-neutral-100 dark:bg-neutral-800 rounded-lg p-5 space-y-2">
          <Badge variant="secondary">Original</Badge>
          <div className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
            {original.width} × {original.height} px
          </div>
          <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
            {formatFileSize(original.fileSize)}
          </div>
        </div>

        {/* Arrow (Desktop only) */}
        <div className="hidden md:flex items-center justify-center text-neutral-400 dark:text-neutral-600">
          <ArrowRight className="h-6 w-6" />
        </div>

        {/* Converted Box (or Placeholder) */}
        {converted ? (
          <div className={cn(
            'rounded-lg p-5 space-y-2',
            isSmaller
              ? 'bg-green-50 dark:bg-green-950/20'
              : 'bg-orange-50 dark:bg-orange-950/20'
          )}>
            <Badge variant={isSmaller ? 'success' : 'warning'}>Converted</Badge>
            <div className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
              {converted.width} × {converted.height} px
            </div>
            <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
              {formatFileSize(converted.fileSize)}
            </div>
          </div>
        ) : (
          <div className="bg-neutral-100 dark:bg-neutral-800 rounded-lg p-5 space-y-2 opacity-50">
            <Badge variant="secondary">Preview</Badge>
            <div className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
              {original.width} × {original.height} px
            </div>
            <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
              ?
            </div>
          </div>
        )}
      </div>

      {/* Compression Rate */}
      {converted && compressionRate && (
        <div className="mt-6 pt-6 border-t border-neutral-200 dark:border-neutral-800 text-center">
          <div
            className={cn(
              'inline-flex items-center gap-2 px-4 py-2 rounded-lg',
              isSmaller
                ? 'bg-green-100 dark:bg-green-900/30'
                : 'bg-orange-100 dark:bg-orange-900/30'
            )}
          >
            <span
              className={cn(
                'text-xl font-bold',
                isSmaller
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-orange-600 dark:text-orange-400'
              )}
            >
              {isSmaller ? '↓' : '↑'} {Math.abs(parseFloat(compressionRate))}%{' '}
              {isSmaller ? t('converter.smaller') : t('converter.larger')}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
